import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Ban, Download, Loader2, Lock, Mail, Pencil, Plus, RefreshCw, Save, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/components/site/EventCard";
import { ManualRegistrationFieldInput, manualFieldIsVisible } from "@/pages/admin/AdminEventTicketTypes";

// The requested "rooster" (schedule) for timeslot bookings ("Sinterklaas
// huisbezoeken"-style events) — one row per timeslot, grouped by date, each
// showing its booking (if any) with a "Wissel" action that moves the booking
// to a different slot and, by default, e-mails the customer about the change.
// A sibling to CompanionPresence.jsx/MultiDayPresence.jsx, never a modification
// of either — this is a structurally different shape (row = timeslot, not
// row = ticket) backed by its own endpoints.
//
// Also supports, directly from this rooster (no need to switch to the
// separate Tijdsloten/Tickets & verkoop tabs): editing a slot's own date/time/
// price/capacity ("Bewerken"), a one-click Blokkeer/Deblokkeer toggle (the
// existing `active` flag — already fully enforced server-side for every new
// booking AND every reschedule target via
// _assert_timeslot_ids_form_consecutive_run, so this UI only exposes an
// already-real mechanism), manually blocking an empty slot with a real
// customer's details plus an e-mail option ("Afspraak handmatig invoeren" —
// reuses the exact same POST /admin/events/{event_id}/tickets/manual endpoint
// ManualTicketSection already uses, just pre-scoped to one clicked slot), and
// an Excel export of the whole rooster (reuses the existing, generic
// GET /admin/reports/{event_id}/export.xlsx — its own new "Huisbezoeken" sheet).

function SwapPanel({ slot, allSlots, authAxios, onDone, onCancel }) {
  const [targetId, setTargetId] = useState("");
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // A blocked (active=false) or full slot is never an actually pickable
  // reschedule target -- the backend already refuses it
  // (_assert_timeslot_ids_form_consecutive_run) -- but it is still SHOWN,
  // disabled, so the admin can see the whole picture (which slots exist and
  // why they can't be chosen) instead of them silently vanishing from the
  // list, same "visible-but-disabled, never hidden" convention already used
  // for a full slot in AdminTimeslotUnitPicker/the order-edit swap list.
  const byDate = allSlots
    .filter((s) => s.id !== slot.id && !s.booking)
    .reduce((acc, s) => {
      (acc[s.date] = acc[s.date] || []).push(s);
      return acc;
    }, {});
  const dates = Object.keys(byDate).sort();

  const submit = async () => {
    if (!targetId) {
      toast.error("Kies een nieuw tijdslot");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await authAxios.patch(`/admin/tickets/${slot.booking.ticket_id}/timeslot`, {
        // Wissel always moves a booking to exactly one new slot -- a booking
        // that itself spans several consecutive slots (two blocks combined
        // into one visit) can be swapped elsewhere via a fresh manual booking
        // instead; this panel deliberately keeps the simpler single-slot move.
        timeslot_ids: [targetId],
        notify_customer: notify,
        message: message.trim() || null,
      });
      if (notify && data.email && !data.email.sent) {
        toast.error(`Tijdslot gewisseld, maar e-mail is niet gelukt: ${data.email.error || "onbekende fout"}`);
      } else {
        toast.success(notify ? "Tijdslot gewisseld en klant gemaild" : "Tijdslot gewisseld");
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2 px-3 py-3 rounded-lg bg-svj-bg border border-svj-cyan/40 space-y-3" data-testid={`timeslot-swap-panel-${slot.id}`}>
      <div className="text-xs font-semibold text-white">Verplaats {slot.booking.holder_name || slot.booking.code} naar…</div>
      <div className="space-y-2 max-h-56 overflow-y-auto">
        {dates.length === 0 && <div className="text-xs text-slate-500">Geen andere vrije tijdsloten beschikbaar.</div>}
        {dates.map((date) => (
          <div key={date}>
            <div className="text-[11px] text-slate-400 mb-1">
              {new Date(`${date}T00:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {byDate[date]
                .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
                .map((s) => {
                  const isBlocked = s.active === false;
                  const isFull = (s.booked_count || 0) >= s.capacity;
                  const unavailable = isBlocked || isFull || !!s.conflict_blocked;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={unavailable}
                      onClick={() => setTargetId(s.id)}
                      data-testid={`timeslot-swap-target-${s.id}`}
                      title={isBlocked ? "Geblokkeerd" : isFull ? "Vol" : s.conflict_blocked ? "Bezet (andere categorie)" : undefined}
                      className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${
                        targetId === s.id
                          ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                          : unavailable
                          ? "bg-svj-card border-svj-border text-red-300"
                          : "bg-svj-card border-svj-border text-slate-300 hover:text-white"
                      }`}
                    >
                      {(s.start_time || "").slice(0, 5)} - {(s.end_time || "").slice(0, 5)}
                      {isBlocked && " · geblokkeerd"}
                      {!isBlocked && isFull && " · vol"}
                    </button>
                  );
                })}
            </div>
          </div>
        ))}
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-300">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        <Mail className="h-3.5 w-3.5" /> Stuur e-mail naar klant over de wijziging
      </label>
      {notify && (
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Optionele notitie bij de e-mail (bv. reden van de wijziging)"
          rows={2}
          className="w-full px-3 py-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
        />
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="rounded-lg border bg-svj-card border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">
          Annuleren
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !targetId}
          data-testid={`timeslot-swap-submit-${slot.id}`}
          className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />} Wissel
        </button>
      </div>
    </div>
  );
}

// "Ook wil ik de tijdsloten kunnen wijzigen" -- edit a slot's own date/time/
// price/capacity/active directly from the rooster, instead of needing to
// switch to the separate Tijdsloten tab. Mirrors AdminEventTimeslots.jsx's
// TimeslotRow edit form exactly (same endpoint, same fields), trimmed for
// inline use here.
function EditSlotPanel({ slot, authAxios, onDone, onCancel }) {
  const [form, setForm] = useState({
    date: slot.date || "",
    start_time: (slot.start_time || "").slice(0, 5),
    end_time: (slot.end_time || "").slice(0, 5),
    price: ((slot.price_cents || 0) / 100).toFixed(2),
    capacity: String(slot.capacity ?? 1),
    active: slot.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = Number.parseFloat(String(form.price).replace(",", "."));
    const priceCents = Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
    setSaving(true);
    try {
      await authAxios.patch(`/admin/timeslots/${slot.id}`, {
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        price_cents: priceCents,
        capacity: Math.max(1, Number(form.capacity) || 1),
        active: form.active,
      });
      toast.success("Tijdslot opgeslagen");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 px-3 py-3 rounded-lg bg-svj-bg border border-svj-cyan/40 space-y-3" data-testid={`timeslot-edit-panel-${slot.id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid={`timeslot-edit-${slot.id}-date`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid={`timeslot-edit-${slot.id}-start`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid={`timeslot-edit-${slot.id}-end`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Prijs (€)" data-testid={`timeslot-edit-${slot.id}-price`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="Capaciteit" data-testid={`timeslot-edit-${slot.id}-capacity`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-300">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid={`timeslot-edit-${slot.id}-active`} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        Actief (beschikbaar om te boeken)
      </label>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="rounded-lg border bg-svj-card border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`timeslot-edit-${slot.id}-save`}
          className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
    </div>
  );
}

function emptyBookForm(fieldDefs, defaultTicketTypeId) {
  return {
    ticket_type_id: defaultTicketTypeId || "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    // Optional -- not every phone-arranged afspraak has these known yet, and
    // the organiser must always be able to fill them in or correct them
    // later via "Afspraak bewerken" instead of being forced to enter them now.
    company_name: "",
    street: "",
    postal_code: "",
    city: "",
    payment_mode: "paid_now",
    defer_payment_link: false,
    send_confirmation_email: true,
    field_values: Object.fromEntries((fieldDefs || []).map((f) => [f.key, f.field_type === "checkbox" ? false : ""])),
  };
}

// "In presentie gewoon een tijdslot kunnen aanklikken en het formulier
// handmatig kunnen invoeren en met een mail optie" -- blocks one or more
// CONSECUTIVE empty slots directly with a real customer's details, via the
// exact same POST /admin/events/{event_id}/tickets/manual endpoint
// ManualTicketSection already uses (a manually-blocked slot is a real
// ticket, same code/PDF/presence behavior). Starts pre-selected on the one
// clicked slot; "Nog een aansluitend tijdslot toevoegen" lets the admin
// extend it to a neighbour (same date+category) for a customer who needs
// more time than one slot -- same toggle/extend/shrink rule
// AdminTimeslotUnitPicker already uses for the public checkout/manual
// ticket form, so both surfaces agree on what "consecutive" means.
function BookSlotPanel({ slot, allSlots, eventId, authAxios, fieldDefs, fieldsById, ticketTypes, onDone, onCancel }) {
  const [form, setForm] = useState(() => emptyBookForm(fieldDefs, ticketTypes.length === 1 ? ticketTypes[0].id : ""));
  const [timeslotIds, setTimeslotIds] = useState([slot.id]);
  const [submitting, setSubmitting] = useState(false);

  // Only the fields relevant to THIS slot's own category -- without this,
  // every category's own custom fields rendered at once (two near-identical
  // fields side by side, e.g. two "Naam kind of namen kinderen"), the exact
  // "dubbele velden" reported from this panel.
  const visibleFieldDefs = useMemo(
    () => fieldDefs.filter((f) => manualFieldIsVisible(f, form.field_values, fieldsById, slot.category_id)),
    [fieldDefs, fieldsById, form.field_values, slot.category_id],
  );

  // Only same date + same category, never booked/blocked/conflict-blocked --
  // exactly the slots _assert_timeslot_ids_form_consecutive_run would accept
  // as part of this run, sorted chronologically.
  const groupSlots = useMemo(
    () =>
      allSlots
        .filter((s) => s.date === slot.date && s.category_id === slot.category_id)
        .filter((s) => s.id === slot.id || (!s.booking && s.active !== false && !s.conflict_blocked))
        .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")),
    [allSlots, slot.date, slot.category_id, slot.id],
  );
  const selectedSlots = useMemo(
    () => timeslotIds.map((id) => groupSlots.find((s) => s.id === id)).filter(Boolean),
    [timeslotIds, groupSlots],
  );
  const firstSelected = selectedSlots[0];
  const lastSelected = selectedSlots[selectedSlots.length - 1];

  const toggleSlot = (s) => {
    if (s.id === slot.id) return; // the anchor slot can't be removed from its own panel
    const slotIdx = groupSlots.findIndex((g) => g.id === s.id);
    const firstIdx = groupSlots.findIndex((g) => g.id === firstSelected.id);
    const lastIdx = groupSlots.findIndex((g) => g.id === lastSelected.id);
    if (timeslotIds.includes(s.id)) {
      if (slotIdx === lastIdx) setTimeslotIds(selectedSlots.slice(0, -1).map((g) => g.id));
      else if (slotIdx === firstIdx) setTimeslotIds(selectedSlots.slice(1).map((g) => g.id));
      // A selected slot in the middle of the range can't be unselected on its
      // own without breaking the consecutive-run rule -- ignored, same as
      // AdminTimeslotUnitPicker.
      return;
    }
    if (slotIdx === lastIdx + 1) setTimeslotIds([...selectedSlots.map((g) => g.id), s.id]);
    else if (slotIdx === firstIdx - 1) setTimeslotIds([s.id, ...selectedSlots.map((g) => g.id)]);
    // Anything else (not adjacent to the current range) is ignored rather
    // than silently jumping the selection elsewhere -- the anchor slot must
    // always stay part of the run in this panel.
  };

  const submit = async () => {
    if (!form.ticket_type_id) return toast.error("Kies een tickettype");
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Voor- en achternaam zijn verplicht");
    if (!form.email.trim()) return toast.error("E-mailadres is verplicht");
    for (const f of visibleFieldDefs) {
      if (f.required && !String(form.field_values[f.key] ?? "").trim()) {
        return toast.error(`Veld '${f.label}' is verplicht`);
      }
    }
    setSubmitting(true);
    try {
      // Only the fields actually shown for this unit's category are sent --
      // a hidden field's value (if any leftover default) never reaches the
      // backend, matching the public checkout's own fieldIsVisible filtering.
      const visibleKeys = new Set(visibleFieldDefs.map((f) => f.key));
      const field_values = Object.fromEntries(Object.entries(form.field_values).filter(([k]) => visibleKeys.has(k)));
      const { data } = await authAxios.post(`/admin/events/${eventId}/tickets/manual`, {
        ticket_type_id: form.ticket_type_id,
        qty: 1,
        customer: {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          company_name: form.company_name.trim() || null,
          street: form.street.trim() || null,
          postal_code: form.postal_code.trim() || null,
          city: form.city.trim() || null,
        },
        send_confirmation_email: !!form.send_confirmation_email,
        free: form.payment_mode === "free",
        send_payment_link: form.payment_mode === "payment_link",
        defer_payment_link: form.payment_mode === "payment_link" && !!form.defer_payment_link,
        timeslot_units: [{ timeslot_ids: timeslotIds, field_values }],
      });
      if (data.mode === "payment_link" && data.deferred) {
        // "Betaallink later versturen" was checked -- the slot/ticket is
        // reserved right now, but NO live Pay.nl/Mollie payment session was
        // started, so there is nothing that can silently expire/fail before
        // the admin gets around to sending it from Bestellingen. Without
        // this branch, every "Stuur betaallink" click here immediately
        // started a real payment session that would quietly fail a while
        // later with zero trace in the rooster -- the exact "het slaat niet
        // goed op" symptom reported from production.
        toast.success("Tijdslot gereserveerd — betaallink kan later verstuurd worden vanuit Bestellingen");
      } else if (data.mode === "payment_link") {
        toast.success(data.email?.sent ? "Betaallink aangemaakt en gemaild" : "Betaallink aangemaakt (mail niet verstuurd)");
      } else {
        const code = (data.tickets || [])[0]?.code;
        toast.success(code ? `Geblokkeerd — ticket ${code} aangemaakt` : "Tijdslot geblokkeerd");
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2 px-3 py-3 rounded-lg bg-svj-bg border border-svj-cyan/40 space-y-3" data-testid={`timeslot-book-panel-${slot.id}`}>
      <div className="text-xs font-semibold text-white">
        Afspraak handmatig invoeren — {(slot.start_time || "").slice(0, 5)} - {(slot.end_time || "").slice(0, 5)}
      </div>
      {groupSlots.length > 1 && (
        <div>
          <span className="text-[10px] text-slate-400">
            Aaneengesloten tijdsloten <span className="normal-case font-normal text-slate-500">(tik een aangrenzend tijdslot aan voor een langer bezoek)</span>
          </span>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {groupSlots.map((s) => {
              const checked = timeslotIds.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleSlot(s)}
                  disabled={s.id === slot.id}
                  data-testid={`timeslot-book-${slot.id}-extend-${s.id}`}
                  className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                    checked
                      ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                      : "bg-svj-card border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  {(s.start_time || "").slice(0, 5)} - {(s.end_time || "").slice(0, 5)}
                </button>
              );
            })}
          </div>
          {selectedSlots.length > 1 && (
            <div className="mt-1 text-[10px] text-svj-cyan">
              {selectedSlots.length} tijdsloten samengevoegd ({(firstSelected.start_time || "").slice(0, 5)} - {(lastSelected.end_time || "").slice(0, 5)}).
            </div>
          )}
        </div>
      )}
      {ticketTypes.length === 0 ? (
        <p className="text-xs text-slate-400">
          Er zijn nog geen tickettypes voor dit event — maak er eerst één aan bij "Tickets &amp; verkoop".
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { value: "paid_now", label: "Al betaald/geregeld" },
              { value: "free", label: "Gratis" },
              { value: "payment_link", label: "Stuur betaallink" },
            ].map((opt) => (
              <label
                key={opt.value}
                data-testid={`timeslot-book-${slot.id}-mode-${opt.value}`}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs cursor-pointer ${
                  form.payment_mode === opt.value ? "border-svj-cyan bg-svj-cyan/10 text-white" : "border-svj-border bg-svj-card text-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name={`timeslot-book-mode-${slot.id}`}
                  checked={form.payment_mode === opt.value}
                  onChange={() => setForm({ ...form, payment_mode: opt.value })}
                  className="h-3.5 w-3.5"
                />
                {opt.label}
              </label>
            ))}
          </div>
          {form.payment_mode === "payment_link" && (
            // Without this, "Stuur betaallink" always started a real Pay.nl/
            // Mollie payment session immediately -- fine for one booking, but
            // the actual use case this panel exists for ("meerdere
            // telefonische boekingen achter elkaar vastleggen, het tijdslot
            // claimen, en de betaallinks later in bulk versturen") had no way
            // to defer that start. An un-acted-on payment session eventually
            // expires/fails on its own, silently releasing the slot again
            // with zero trace in the rooster -- reported from production as
            // "het slaat de afspraken niet goed op in presentie".
            <label className="flex items-start gap-2 rounded-lg border border-svj-border bg-svj-card px-2.5 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.defer_payment_link}
                onChange={(e) => setForm({ ...form, defer_payment_link: e.target.checked })}
                data-testid={`timeslot-book-${slot.id}-defer`}
                className="mt-0.5 h-4 w-4 rounded border-svj-border bg-svj-bg"
              />
              <span className="text-xs text-slate-300">
                <span className="font-semibold text-white">Betaallink later versturen.</span> Reserveert het
                tijdslot meteen, maar stuurt nog geen betaallink of mail — handig om eerst meerdere afspraken
                achter elkaar vast te leggen en de betaallinks later in bulk te versturen (via Bestellingen).
              </span>
            </label>
          )}
          {ticketTypes.length > 1 && (
            <select
              value={form.ticket_type_id}
              onChange={(e) => setForm({ ...form, ticket_type_id: e.target.value })}
              data-testid={`timeslot-book-${slot.id}-type`}
              className="w-full h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              <option value="">Kies een tickettype…</option>
              {ticketTypes.map((tt) => (
                <option key={tt.id} value={tt.id}>{tt.name}</option>
              ))}
            </select>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder="Voornaam *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid={`timeslot-book-${slot.id}-first-name`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input placeholder="Achternaam *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid={`timeslot-book-${slot.id}-last-name`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input type="email" placeholder="E-mail *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`timeslot-book-${slot.id}-email`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input placeholder="Telefoon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={`timeslot-book-${slot.id}-phone`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </div>
          {/* All optional -- never known yet for a phone-arranged afspraak, and
              always correctable later via "Afspraak bewerken", so none of
              these block submitting the booking now. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder="Bedrijfsnaam" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid={`timeslot-book-${slot.id}-company`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input placeholder="Straat + nr" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} data-testid={`timeslot-book-${slot.id}-street`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input placeholder="Postcode" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid={`timeslot-book-${slot.id}-postal`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input placeholder="Plaats" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid={`timeslot-book-${slot.id}-city`} className="h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </div>
          {visibleFieldDefs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {visibleFieldDefs.map((f) => (
                <label key={f.key} className="block">
                  <span className="text-[10px] text-slate-400">{f.label}{f.required && " *"}</span>
                  <ManualRegistrationFieldInput
                    field={f}
                    value={form.field_values[f.key]}
                    idPrefix={`timeslot-book-${slot.id}-field-${f.key}`}
                    onChange={(v) => setForm({ ...form, field_values: { ...form.field_values, [f.key]: v } })}
                  />
                </label>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={form.send_confirmation_email} onChange={(e) => setForm({ ...form, send_confirmation_email: e.target.checked })} data-testid={`timeslot-book-${slot.id}-send-email`} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
            <Mail className="h-3.5 w-3.5" /> {form.payment_mode === "payment_link" ? "Betaallink mailen naar klant" : "Bevestigingsmail versturen"}
          </label>
        </>
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="rounded-lg border bg-svj-card border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        {ticketTypes.length > 0 && (
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid={`timeslot-book-${slot.id}-submit`}
            className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Blokkeer
          </button>
        )}
      </div>
    </div>
  );
}

// "Een bestaande afspraak in presentie kan ik niet wijzigen maak dit ook
// mogelijk" -- edits an EXISTING booking's own participant name/custom-field
// answers plus the order's contact e-mail/phone, directly from the rooster.
// Reuses the existing PATCH /admin/tickets/{ticket_id}/participant (name +
// registration_field_values -- the same endpoint AdminReports.jsx's own
// order-edit panel already uses) and PATCH /admin/orders/{order_id}
// (customer e-mail/phone) -- no new backend endpoint needed. Fields are
// filtered by this slot's own category, the exact same rule BookSlotPanel
// uses, so editing a booking never shows another category's near-identical
// fields either.
function EditBookingPanel({ slot, fieldDefs, fieldsById, authAxios, onDone, onCancel }) {
  const booking = slot.booking;
  const [form, setForm] = useState({
    holder_name: booking.holder_name || "",
    customer_email: booking.customer_email || "",
    customer_phone: booking.customer_phone || "",
    // Optional -- same reasoning as BookSlotPanel's own fields: often not
    // known yet when the booking was first entered, so this is where they
    // get added or corrected.
    customer_company_name: booking.customer_company_name || "",
    customer_street: booking.customer_street || "",
    customer_postal_code: booking.customer_postal_code || "",
    customer_city: booking.customer_city || "",
    field_values: { ...(booking.field_values || {}) },
  });
  const [saving, setSaving] = useState(false);

  const visibleFieldDefs = useMemo(
    () => fieldDefs.filter((f) => manualFieldIsVisible(f, form.field_values, fieldsById, slot.category_id)),
    [fieldDefs, fieldsById, form.field_values, slot.category_id],
  );

  const save = async () => {
    if (!form.holder_name.trim()) {
      toast.error("Naam is verplicht");
      return;
    }
    setSaving(true);
    try {
      // Only the fields visible for this booking's own category are sent --
      // a leftover value from a field no longer shown never reaches the
      // backend (same convention as BookSlotPanel's submit).
      const visibleKeys = new Set(visibleFieldDefs.map((f) => f.key));
      const field_values = Object.fromEntries(Object.entries(form.field_values).filter(([k]) => visibleKeys.has(k)));
      await authAxios.patch(`/admin/tickets/${booking.ticket_id}/participant`, {
        holder_name: form.holder_name.trim(),
        registration_field_values: field_values,
      });
      // Customer e-mail/phone live on the order, not the ticket -- a second,
      // small PATCH, only attempted when we actually know the order id
      // (always true for a real booking; defensive for an edge case).
      if (booking.order_id) {
        await authAxios.patch(`/admin/orders/${booking.order_id}`, {
          customer_email: form.customer_email.trim() || null,
          customer_phone: form.customer_phone.trim() || null,
          customer_company_name: form.customer_company_name.trim() || null,
          customer_street: form.customer_street.trim() || null,
          customer_postal_code: form.customer_postal_code.trim() || null,
          customer_city: form.customer_city.trim() || null,
        });
      }
      toast.success("Afspraak bijgewerkt");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 px-3 py-3 rounded-lg bg-svj-bg border border-svj-cyan/40 space-y-3" data-testid={`timeslot-editbooking-panel-${slot.id}`}>
      <div className="text-xs font-semibold text-white">
        Afspraak bewerken — <code className="text-[11px]">{booking.code}</code>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="block">
          <span className="text-[10px] text-slate-400">Naam</span>
          <input
            value={form.holder_name}
            onChange={(e) => setForm({ ...form, holder_name: e.target.value })}
            data-testid={`timeslot-editbooking-${slot.id}-name`}
            className="mt-1 w-full h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-400">E-mail</span>
          <input
            type="email"
            value={form.customer_email}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            data-testid={`timeslot-editbooking-${slot.id}-email`}
            className="mt-1 w-full h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-400">Telefoon</span>
          <input
            value={form.customer_phone}
            onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
            data-testid={`timeslot-editbooking-${slot.id}-phone`}
            className="mt-1 w-full h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
      </div>
      {/* All optional -- add or correct these whenever they become known,
          never required to save the rest of the booking. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] text-slate-400">Bedrijfsnaam</span>
          <input
            value={form.customer_company_name}
            onChange={(e) => setForm({ ...form, customer_company_name: e.target.value })}
            data-testid={`timeslot-editbooking-${slot.id}-company`}
            className="mt-1 w-full h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-400">Straat + nr</span>
          <input
            value={form.customer_street}
            onChange={(e) => setForm({ ...form, customer_street: e.target.value })}
            data-testid={`timeslot-editbooking-${slot.id}-street`}
            className="mt-1 w-full h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-400">Postcode</span>
          <input
            value={form.customer_postal_code}
            onChange={(e) => setForm({ ...form, customer_postal_code: e.target.value })}
            data-testid={`timeslot-editbooking-${slot.id}-postal`}
            className="mt-1 w-full h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-400">Plaats</span>
          <input
            value={form.customer_city}
            onChange={(e) => setForm({ ...form, customer_city: e.target.value })}
            data-testid={`timeslot-editbooking-${slot.id}-city`}
            className="mt-1 w-full h-9 px-2 rounded-lg bg-svj-card border border-svj-border text-white text-xs focus:outline-none focus:ring-2 focus:ring-svj-cyan"
          />
        </label>
      </div>
      {visibleFieldDefs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {visibleFieldDefs.map((f) => (
            <label key={f.key} className="block">
              <span className="text-[10px] text-slate-400">
                {f.label}
                {f.required && " *"}
              </span>
              <ManualRegistrationFieldInput
                field={f}
                value={form.field_values[f.key]}
                idPrefix={`timeslot-editbooking-${slot.id}-field-${f.key}`}
                onChange={(v) => setForm({ ...form, field_values: { ...form.field_values, [f.key]: v } })}
              />
            </label>
          ))}
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="rounded-lg border bg-svj-card border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`timeslot-editbooking-${slot.id}-save`}
          className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
    </div>
  );
}

function centsToEuroInput(cents) {
  return ((cents || 0) / 100).toFixed(2);
}
function euroInputToCents(value) {
  const n = Number.parseFloat(String(value).replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
}

// "Wat ook niet kan is bijvoorbeeld een ander tijdstip invoeren" -- a brand
// new, fully custom-time slot (e.g. 16:15-17:15 for one specific company),
// not constrained to the bulk-generated grid. Mirrors
// AdminEventTimeslots.jsx's own TimeslotAddForm exactly (same endpoint, same
// fields, same "category only prefills, never locks, date/time/price/
// capacity" behavior) so an admin never has to leave Presentie for this.
function AddSlotPanel({ eventId, authAxios, categoryOptions, onCancel, onCreated }) {
  const [form, setForm] = useState({ date: "", start_time: "", end_time: "", price: "0.00", capacity: "1", category_id: "" });
  const [creating, setCreating] = useState(false);

  const applyCategory = (categoryId) => {
    const cat = categoryOptions.find((c) => c.id === categoryId);
    if (!cat) {
      setForm({ ...form, category_id: "" });
      return;
    }
    let end = form.end_time;
    if (form.start_time) {
      const [h, m] = form.start_time.split(":").map(Number);
      const total = h * 60 + m + cat.duration_minutes;
      end = `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
    }
    setForm({ ...form, category_id: categoryId, price: centsToEuroInput(cat.price_cents), end_time: end });
  };

  const create = async () => {
    if (!form.date || !form.start_time || !form.end_time) {
      toast.error("Datum, starttijd en eindtijd zijn verplicht");
      return;
    }
    setCreating(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/timeslots`, {
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        price_cents: euroInputToCents(form.price),
        capacity: Math.max(1, Number(form.capacity) || 1),
        category_id: form.category_id || null,
      });
      toast.success("Tijdslot toegevoegd");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mb-4 px-3 py-3 rounded-lg bg-svj-bg border border-svj-cyan/40 space-y-2" data-testid="timeslot-schedule-add-panel">
      <div className="text-xs font-semibold text-white">Nieuw tijdslot toevoegen</div>
      {categoryOptions.length > 0 && (
        <select
          value={form.category_id}
          onChange={(e) => applyCategory(e.target.value)}
          data-testid="timeslot-schedule-add-category"
          className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm w-full"
        >
          <option value="">Geen categorie</option>
          {categoryOptions.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({centsToEuroInput(c.price_cents)} € / {c.duration_minutes} min)</option>
          ))}
        </select>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" data-testid="timeslot-schedule-add-date" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
        <input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} type="time" data-testid="timeslot-schedule-add-start" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
        <input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} type="time" data-testid="timeslot-schedule-add-end" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
        <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} type="number" step="0.01" min="0" placeholder="Prijs (€)" data-testid="timeslot-schedule-add-price" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
        <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} type="number" min="1" placeholder="Capaciteit" data-testid="timeslot-schedule-add-capacity" className="h-9 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm" />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="rounded-lg border bg-svj-card border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
        <button
          type="button"
          onClick={create}
          disabled={creating}
          data-testid="timeslot-schedule-add-submit"
          className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
        </button>
      </div>
    </div>
  );
}

export default function TimeslotSchedule({ eventId, authAxios, timeslotManualBookingsEnabled = false }) {
  const [slots, setSlots] = useState([]);
  const [categoriesById, setCategoriesById] = useState({});
  const [fieldDefs, setFieldDefs] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Which row has an open inline panel, and which one -- only one panel open
  // at a time across the whole rooster, never several on top of each other.
  const [expanded, setExpanded] = useState(null); // { id, mode: "swap" | "edit" | "book" | "editBooking" } | null
  const [blockingId, setBlockingId] = useState(null); // slot id while a quick blokkeer/deblokkeer toggle is in flight
  const [exporting, setExporting] = useState(false);
  const [addingSlot, setAddingSlot] = useState(false);
  // "Overzichtelijker" for an event with many generated slots (dozens per
  // day, several categories): default to only showing booked slots -- a
  // rooster is read mainly to see who's coming, not to browse the open
  // inventory, which already has its own view in Tijdsloten. Both filters
  // are opt-out (toggle/select back to "everything"), never a second source
  // of truth -- they only narrow which already-loaded rows render.
  const [onlyBooked, setOnlyBooked] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [collapsedDates, setCollapsedDates] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const requests = [
        authAxios.get(`/admin/events/${eventId}/timeslot-schedule`),
        authAxios.get(`/admin/events/${eventId}/timeslot-categories`),
        // Registration fields are needed to edit an EXISTING booking's custom
        // answers too, not only to create a new manual one -- a booking can
        // carry these from a real checkout regardless of whether manual
        // blocking is enabled, so this is fetched unconditionally.
        authAxios.get(`/admin/events/${eventId}/registration-fields`),
      ];
      // Ticket types are only needed to CREATE a new manual booking -- never
      // requested for an event where that sub-flag is off.
      if (timeslotManualBookingsEnabled) {
        requests.push(authAxios.get(`/admin/events/${eventId}/ticket-types`));
      }
      const [scheduleRes, categoriesRes, fieldsRes, typesRes] = await Promise.all(requests);
      setSlots(scheduleRes.data.items || []);
      setCategoriesById(Object.fromEntries((categoriesRes.data.items || []).map((c) => [c.id, c])));
      setFieldDefs((fieldsRes?.data.items || []).filter((f) => f.active));
      setTicketTypes(timeslotManualBookingsEnabled ? (typesRes?.data.items || []) : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, timeslotManualBookingsEnabled]);

  const downloadExport = async () => {
    setExporting(true);
    try {
      const resp = await authAxios.get(`/admin/reports/${eventId}/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `huisbezoeken-${eventId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Export mislukt");
    } finally {
      setExporting(false);
    }
  };

  const toggleBlocked = async (slot) => {
    const nextActive = slot.active === false; // currently blocked -> unblock, and vice versa
    // A block that nobody can later explain is exactly the production
    // confusion this asks for one short, optional reason up front -- the
    // backend records who/when regardless, this is just the "why".
    let reason = null;
    if (!nextActive) {
      reason = window.prompt("Reden voor blokkeren (optioneel, bv. 'pauze Sinterklaas'):") || null;
    }
    setBlockingId(slot.id);
    try {
      await authAxios.patch(`/admin/timeslots/${slot.id}`, { active: nextActive, reason });
      toast.success(nextActive ? "Tijdslot gedeblokkeerd" : "Tijdslot geblokkeerd");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBlockingId(null);
    }
  };

  if (loading && slots.length === 0) {
    return (
      <div className="text-sm text-slate-400 inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Rooster laden…
      </div>
    );
  }

  if (!loading && slots.length === 0) {
    return <div className="text-sm text-slate-400">Nog geen tijdsloten voor dit event.</div>;
  }

  const totalBookings = slots.filter((s) => s.booking).length;
  const categoryOptions = Object.values(categoriesById);
  const fieldsById = Object.fromEntries(fieldDefs.map((f) => [f.id, f]));

  // "Alleen bezette tijdsloten tonen" must also keep a manually-blocked slot
  // (active=false, no real booking) visible -- a blocked slot is not
  // bookable either, so hiding it under this filter made it silently
  // disappear from the rooster entirely (the reported gap).
  const visibleSlots = slots.filter(
    (s) => (!onlyBooked || !!s.booking || s.active === false) && (!categoryFilter || s.category_id === categoryFilter),
  );
  const byDate = visibleSlots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort();

  const toggleCollapsed = (date) => {
    setCollapsedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const closeExpanded = () => setExpanded(null);
  const onPanelDone = () => {
    setExpanded(null);
    load();
  };

  return (
    <div data-testid="timeslot-schedule">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs text-slate-400">
          {totalBookings} boeking{totalBookings === 1 ? "" : "en"} · {slots.length} tijdslot{slots.length === 1 ? "" : "en"}
        </div>
        <div className="flex items-center gap-3">
          {!addingSlot && (
            <button
              type="button"
              onClick={() => setAddingSlot(true)}
              data-testid="timeslot-schedule-add-toggle"
              className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" /> Nieuw tijdslot
            </button>
          )}
          <button
            type="button"
            onClick={downloadExport}
            disabled={exporting}
            data-testid="timeslot-schedule-export"
            className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Excel export
          </button>
          <button type="button" onClick={load} data-testid="timeslot-schedule-refresh" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Vernieuwen
          </button>
        </div>
      </div>
      {addingSlot && (
        <AddSlotPanel
          eventId={eventId}
          authAxios={authAxios}
          categoryOptions={categoryOptions}
          onCancel={() => setAddingSlot(false)}
          onCreated={() => {
            setAddingSlot(false);
            load();
          }}
        />
      )}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2 rounded-lg border border-svj-border bg-svj-bg/40 px-3 py-2">
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={onlyBooked}
            onChange={(e) => setOnlyBooked(e.target.checked)}
            data-testid="timeslot-schedule-only-booked"
            className="h-4 w-4 rounded border-svj-border bg-svj-bg"
          />
          Alleen bezette of geblokkeerde tijdsloten tonen
        </label>
        {categoryOptions.length > 0 && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="timeslot-schedule-category-filter"
            className="h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white text-xs"
          >
            <option value="">Alle categorieën</option>
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
      </div>
      {dates.length === 0 && (
        <div className="text-sm text-slate-400">
          {onlyBooked ? "Nog geen boekingen of geblokkeerde tijdsloten." : "Geen tijdsloten voor deze filter."}
        </div>
      )}
      <div className="space-y-5">
        {dates.map((date) => {
          const dateSlots = byDate[date];
          const dateBookings = dateSlots.filter((s) => s.booking).length;
          const collapsed = collapsedDates.has(date);
          return (
          <div key={date}>
            <button
              type="button"
              onClick={() => toggleCollapsed(date)}
              data-testid={`timeslot-schedule-date-toggle-${date}`}
              className="w-full flex items-center justify-between gap-2 text-left mb-2"
            >
              <span className="text-xs font-semibold text-slate-300">
                {new Date(`${date}T00:00:00`).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </span>
              <span className="text-[11px] text-slate-500">
                {dateBookings}/{dateSlots.length} geboekt {collapsed ? "▸" : "▾"}
              </span>
            </button>
            {!collapsed && (
            <div className="space-y-2">
              {dateSlots
                .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
                .map((s) => {
                  const isBlocked = s.active === false;
                  // A different category's slot at the exact same clock time
                  // is already booked -- Sinterklaas can't be in two places
                  // at once, so this slot is unbookable too even though
                  // nothing is booked ON it specifically (conflict_blocked,
                  // computed server-side, same rule the booking RPC already
                  // enforces at reservation time).
                  const isConflictBlocked = !s.booking && !isBlocked && !!s.conflict_blocked;
                  const panelMode = expanded && expanded.id === s.id ? expanded.mode : null;
                  return (
                  <div key={s.id} data-testid={`timeslot-schedule-row-${s.id}`}>
                    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border">
                      <div className="min-w-0 flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-white font-semibold">
                          {(s.start_time || "").slice(0, 5)} - {(s.end_time || "").slice(0, 5)}
                        </span>
                        <span className="text-xs text-slate-400">{formatPrice(s.price_cents)}</span>
                        {s.category_id && categoriesById[s.category_id] && (
                          <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold bg-svj-cyan/15 text-svj-cyan">
                            {categoriesById[s.category_id].name}
                          </span>
                        )}
                        {s.booking && s.booking.pending ? (
                          <span className="text-xs text-amber-400 font-medium inline-flex items-center gap-1" data-testid={`timeslot-schedule-pending-${s.id}`}>
                            <Lock className="h-3 w-3" /> {s.booking.holder_name || "Onbekend"} · in afwachting van betaling
                          </span>
                        ) : s.booking ? (
                          <span className="text-xs text-svj-cyan font-medium">
                            {s.booking.holder_name || "Onbekend"} · <code className="text-[11px]">{s.booking.code}</code>
                          </span>
                        ) : isBlocked ? (
                          <span
                            className="text-xs text-red-400 font-medium inline-flex items-center gap-1"
                            data-testid={`timeslot-schedule-blocked-${s.id}`}
                            // The real "wie/wanneer/waarom" answer this whole audit
                            // trail exists for -- a block with no record (made
                            // before this round) still just says "Geblokkeerd".
                            title={
                              s.blocked_at
                                ? `Geblokkeerd door ${s.blocked_by_name || "onbekend"} op ${new Date(s.blocked_at).toLocaleString("nl-NL")}${s.blocked_reason ? ` — ${s.blocked_reason}` : ""}`
                                : undefined
                            }
                          >
                            <Lock className="h-3 w-3" /> Geblokkeerd
                          </span>
                        ) : isConflictBlocked ? (
                          <span className="text-xs text-amber-400 font-medium inline-flex items-center gap-1" data-testid={`timeslot-schedule-conflict-${s.id}`}>
                            <Lock className="h-3 w-3" /> Bezet (andere categorie)
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500">Vrij</span>
                        )}
                        {s.booking && isBlocked && (
                          <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold bg-red-500/15 text-red-300">
                            Geblokkeerd voor nieuwe boekingen
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {/* A pending (not-yet-paid) booking has no real ticket
                            yet -- Wissel/Afspraak bewerken both target a real
                            ticket id, so they only make sense once this slot's
                            booking is backed by one. */}
                        {s.booking && !s.booking.pending && (
                          <button
                            type="button"
                            onClick={() => setExpanded(panelMode === "editBooking" ? null : { id: s.id, mode: "editBooking" })}
                            data-testid={`timeslot-schedule-editbooking-${s.id}`}
                            className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1"
                          >
                            <Pencil className="h-3.5 w-3.5" /> Afspraak bewerken
                          </button>
                        )}
                        {s.booking && !s.booking.pending && (
                          <button
                            type="button"
                            onClick={() => setExpanded(panelMode === "swap" ? null : { id: s.id, mode: "swap" })}
                            data-testid={`timeslot-schedule-swap-${s.id}`}
                            className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1"
                          >
                            <ArrowLeftRight className="h-3.5 w-3.5" /> Wissel
                          </button>
                        )}
                        {!s.booking && timeslotManualBookingsEnabled && !isBlocked && !isConflictBlocked && (
                          <button
                            type="button"
                            onClick={() => setExpanded(panelMode === "book" ? null : { id: s.id, mode: "book" })}
                            data-testid={`timeslot-schedule-book-${s.id}`}
                            className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1"
                          >
                            <UserPlus className="h-3.5 w-3.5" /> Afspraak invoeren
                          </button>
                        )}
                        {!s.booking && (
                          <button
                            type="button"
                            onClick={() => toggleBlocked(s)}
                            disabled={blockingId === s.id}
                            data-testid={`timeslot-schedule-toggle-blocked-${s.id}`}
                            className={`rounded-lg border text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50 ${
                              isBlocked
                                ? "bg-red-500/10 border-red-500/40 text-red-300 hover:text-red-200"
                                : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                            }`}
                          >
                            {blockingId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isBlocked ? <Lock className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                            {isBlocked ? "Deblokkeer" : "Blokkeer"}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpanded(panelMode === "edit" ? null : { id: s.id, mode: "edit" })}
                          data-testid={`timeslot-schedule-edit-${s.id}`}
                          className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Bewerken
                        </button>
                      </div>
                    </div>
                    {panelMode === "editBooking" && s.booking && !s.booking.pending && (
                      <EditBookingPanel slot={s} fieldDefs={fieldDefs} fieldsById={fieldsById} authAxios={authAxios} onCancel={closeExpanded} onDone={onPanelDone} />
                    )}
                    {panelMode === "swap" && s.booking && !s.booking.pending && (
                      <SwapPanel slot={s} allSlots={slots} authAxios={authAxios} onCancel={closeExpanded} onDone={onPanelDone} />
                    )}
                    {panelMode === "edit" && (
                      <EditSlotPanel slot={s} authAxios={authAxios} onCancel={closeExpanded} onDone={onPanelDone} />
                    )}
                    {panelMode === "book" && !s.booking && (
                      <BookSlotPanel
                        slot={s}
                        allSlots={slots}
                        eventId={eventId}
                        authAxios={authAxios}
                        fieldDefs={fieldDefs}
                        fieldsById={fieldsById}
                        ticketTypes={ticketTypes}
                        onCancel={closeExpanded}
                        onDone={onPanelDone}
                      />
                    )}
                  </div>
                  );
                })}
            </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
